import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { reduxConnect } from '../../common';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { smallMediaQuery, mediumMediaQuery, largeMediaQuery } from '../../common/utilities/sizeHelpers';
import { CloseIcon } from '../common/utilities/closeIcon';
import { SnowSkiMap } from './snowSkiMap';
import { IconWidget } from '@weatherbug/react';
import { translate } from  '../../common/translation/translate';
import { TwoColumnDataDisplayRow } from './twoColumnDataDisplayRow';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { ngDeps } from '../../common/utilities/ngDependencies';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

const bigAdSlotId = 'life/snow-ski-detail_x16';
const bigAdHeight = 600;
const bigAdWidth = 300;
const smallAdSlotId = 'life/snow-ski-detail_x13';
const smallAdHeight = 250;
const smallAdWidth = 300;

const mapStoreStateToProps = storeState => {
    const appState = storeState.appState.appState || {};
    const { user } = appState || {};

    const { SlugName: slugName } = user?.ActiveLocation?.Locality || {};

    return {
        slugName
    };
};

const mapDispatchToProps = {};

// eslint-disable-next-line react/display-name
export const ResortDetails = reduxConnect( mapStoreStateToProps, mapDispatchToProps )(withScreenSize(memo(({
    index,
    resortName,
    baseSnow,
    newSnow,
    reported,
    openStatusFlag,
    openStatus,
    totalLifts,
    openLifts,
    snowCondition,
    contentMgmtBaseUrl,
    forecastIconResourceList,
    forecastIconSize,
    forecastIconSet,
    authId,
    secret,
    stdParameters,
    temperature,
    iconCode,
    feelsLike,
    highTemp,
    lowTemp,
    tempUnitAbbreviation,
    summitTemp,
    baseTemp,
    summitVisibility,
    baseVisibility,
    summitWindGust,
    baseWindGust,
    openTrails,
    totalTrails,
    summit,
    base,
    verticalDrop,
    nordic,
    skiing,
    snowPark,
    indoorResort,
    glacierResort,
    summerActivities,
    street,
    city,
    stateName,
    zip,
    phone800,
    website,
    resortLatitude,
    resortLongitude,
    tempUnit,
    windSpeedUnitAbbreviation,
    pressureUnit,
    lengthUnitAbbreviation,
    isDesktop,
    slugName
}) => {
    // TECHDEBT this is another ngDeps "hack", but I don't have a quick way to make this happen:
    // the original directive code did the exact same thing, and I don't know why the details page
    // was the exception to the app-wide rule, but here it is again

    const snowSkiHref = `/life/snow-ski/${slugName}`;
    const openTrailsText = openTrails + translate('of') + totalTrails;
    const openLiftsText = openLifts + translate('of') + totalLifts;
    const nordicText = nordic ? translate('yes') : translate('no');
    const skiingText = skiing ? translate('yes') : translate('no');
    const snowParkText = snowPark ? translate('yes') : translate('no');
    const indoorResortText = indoorResort ? translate('yes') : translate('no');
    const glacierResortText = glacierResort ? translate('yes') : translate('no');
    const summerActivitiesText = summerActivities ? translate('yes') : translate('no');

    return (
        <>
            <BackIconContainer href={snowSkiHref}>
                <CloseIcon width={60} height={60} strokeColor='#ffffff' fillColor='#ffffff' />
            </BackIconContainer>
            <BigContainer>
                <PageContainer>
                    <Info>
                        <MapContainer>
                            <SnowSkiMap
                                latitude={resortLatitude}
                                longitude={resortLongitude}
                            />
                        </MapContainer>
                        <LeftColumn>
                            <Name>
                                {resortName}
                            </Name>
                            <TempContainer>
                                <Temp>{temperature}°</Temp>
                                <IconWrapper>
                                    <IconWidget
                                        iconCode={iconCode}
                                        iconSet={forecastIconSet}
                                        iconSize={forecastIconSize}
                                        authId={authId}
                                        secret={secret}
                                        contentManagementBaseUrl={contentMgmtBaseUrl}
                                        stdParameters={stdParameters}
                                        resourceList={forecastIconResourceList}
                                    />
                                </IconWrapper>
                            </TempContainer>
                            <FeelsLike>{translate('feelsLike')} {feelsLike}°</FeelsLike>
                            <DataRowContainer>
                                <TwoColumnDataDisplayRow title={translate('resortStatus')} value={openStatus} openStatusFlag={openStatusFlag} />
                                <TwoColumnDataDisplayRow title={translate('newSnow')} value={newSnow} />
                                <TwoColumnDataDisplayRow title={translate('reportedSnow')} value={reported} />
                                <SectionHeader>{translate('snowReportTitle')}</SectionHeader>
                                <TwoColumnDataDisplayRow title={translate('snowCondition')} value={snowCondition} />
                                <TwoColumnDataDisplayRow title={translate('snowDepth')} value={baseSnow} />
                                <SectionHeader>{translate('weatherReportTitle')}</SectionHeader>
                                <TwoColumnDataDisplayRow title={translate('summitTemp')} value={summitTemp} unit={'°'} />
                                <TwoColumnDataDisplayRow title={translate('baseTemp')} value={baseTemp} unit={'°'} />
                                <TwoColumnDataDisplayRow title={translate('summitVisibility')} value={summitVisibility} />
                                <TwoColumnDataDisplayRow title={translate('baseVisibility')} value={baseVisibility} />
                                <TwoColumnDataDisplayRow title={translate('summitWindGust')} value={summitWindGust} />
                                <TwoColumnDataDisplayRow title={translate('baseWindGust')} value={baseWindGust} />
                            </DataRowContainer>
                        </LeftColumn>
                        <MiddleColumn>
                            <SectionHeader>{translate('trailsReportTitle')}</SectionHeader>
                            <TwoColumnDataDisplayRow title={translate('openTrails')} value={openTrailsText} />
                            <SectionHeader>{translate('liftReportTitle')}</SectionHeader>
                            <TwoColumnDataDisplayRow title={translate('openLifts')} value={openLiftsText} />
                            <SectionHeader>{translate('elevationTitle')}</SectionHeader>
                            <TwoColumnDataDisplayRow title={translate('summit')} value={summit} />
                            <TwoColumnDataDisplayRow title={translate('base')} value={base} />
                            <TwoColumnDataDisplayRow title={translate('verticalDrop')} value={verticalDrop} />

                            <SectionHeader>{translate('serviceInfoTitle')}</SectionHeader>
                            <TwoColumnDataDisplayRow title={translate('nordic')} value={nordicText} />
                            <TwoColumnDataDisplayRow title={translate('skiing')} value={skiingText} />
                            <TwoColumnDataDisplayRow title={translate('snowPark')} value={snowParkText} />
                            <TwoColumnDataDisplayRow title={translate('indoorResort')} value={indoorResortText} />
                            <TwoColumnDataDisplayRow title={translate('glacierResort')} value={glacierResortText} />
                            <TwoColumnDataDisplayRow title={translate('summerActivities')} value={summerActivitiesText} />
                        </MiddleColumn>
                        <RightColumn>
                            <SmallAdContainer>
                                <DisplayAdUnit
                                    slotId={smallAdSlotId}
                                    height={smallAdHeight}
                                    width={smallAdWidth}
                                />
                            </SmallAdContainer>
                            <AddressContainer>
                                <AddressRow>{resortName}</AddressRow>
                                <AddressRow>{street}</AddressRow>
                                <AddressRow>{city}, {stateName}</AddressRow>
                                <AddressRow>{zip}</AddressRow>
                                <AddressRow>{phone800}</AddressRow>
                                <WebsiteRow href={website}>Website</WebsiteRow>
                            </AddressContainer>
                        </RightColumn>
                    </Info>
                </PageContainer>
                {isDesktop
                    && <BigAdContainer>
                        <DisplayAdUnit
                            slotId={bigAdSlotId}
                            height={bigAdHeight}
                            width={bigAdWidth}
                        />
                    </BigAdContainer>
                }
            </BigContainer>
        </>
    );
})));

ResortDetails.displayName = 'ResortDetails';
ResortDetails.propTypes = {
    latitude: PropTypes.number,
    longitude: PropTypes.number
};

const BigContainer = styled.div`
    display: flex;
    flex-direction: row;
    font-weight: 600;
    @media ${largeMediaQuery} {
        width: 100%;
    }
`;

const PageContainer = styled.div`
    display: block;
    min-width: 100%;
    @media ${largeMediaQuery} {
        min-width: calc(100% - 316px);
    }
`;

const BackIconContainer = styled.a`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    @media ${largeMediaQuery} {
        width: calc(100% - 316px);
    }
`;

const MapContainer = styled.div`
    padding: 6px 0 0 3px;
`;

const Info = styled .div`
    background-color: white;
`;

const LeftColumn = styled.div`
    display: inline-block;
    padding: 0 0 40px 30px;
    font-size: 12pt;
    font-style: normal;
    width: 43%;

    @media ${mediumMediaQuery} {
        width: 50%;
    }
    @media ${smallMediaQuery} {
        width: 100%;
    }
`;

const MiddleColumn = styled.div`
    display: inline-block;
    font-size: 12pt;
    font-style: normal;
    padding-left: 20px;
    width: 43%;

    @media ${mediumMediaQuery} {
        width: 50%;
    }
    @media ${smallMediaQuery} {
        display: block;
        width: 100%;
    }
`;

const RightColumn = styled.div`
    padding: 0 30px 40px 0;
    display: block;
    width: 100%;

    @media ${largeMediaQuery} {
        display: inline-block;
    }
`;

const Name = styled.h1`
    color: #555;
    font-size: 26pt;
    font-style: normal;
    padding-top: 15px;
`;

const TempContainer = styled.div`
    display:flex;
    flex-direction: row;
    padding: 10px 0 0 10px;
`;

const Temp = styled.div`
    font-size: 36px;
    font-style: normal;
    color: black;
`;

const IconWrapper = styled.div`
    margin-left: 100px;
    & .wb-content-mgt-icon-image {
            width: 60px;
            height: 60px;
    }
`;

const FeelsLike = styled.div`
    font-size: 12pt;
    color: #435560;
    margin-top: -20px;
    padding-left: 10px;
`;

const DataRowContainer = styled.div`
    font-size: 12pt;
    margin-top: 35px;
`;

const SectionHeader = styled.h2`
    font-size: 14pt;
    font-style: normal;
    color: #555;
    margin-bottom: 5px;
`;

const SmallAdContainer = styled.div`
    padding-bottom: 100px;
`;

const AddressContainer = styled.div`
    text-align: center;
    font-size: 14px;
`;

const AddressRow = styled.div`
    color: #808080;
`;

const WebsiteRow = styled.a`
    text-decoration: underline;
`;

const BigAdContainer = styled.div`
    padding: 0 0 0 16px;
    float: right;
`;